import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getAdminDashboardApi } from "api/admin";
import {
  C,
  StatCard,
  Panel,
  Row,
  SkeletonRows,
  Empty,
  MatchStatusChip,
  ServiceStatusChip,
  UserTypeChip,
  relativeTime,
  ghostBtn,
} from "./admin_ui";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      const r = await getAdminDashboardApi();
      if (cancelled) return;
      if (r?.success) setData(r);
      else setError(r?.error || "데이터를 불러오지 못했습니다.");
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const stats = data?.stats || {};
  const recent = data?.recent || {};

  return (
    <>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 24,
              fontWeight: 800,
              color: C.ink,
              letterSpacing: "-0.02em",
            }}
          >
            대시보드
          </div>
          <div style={{ marginTop: 4, fontSize: 13, color: C.sub }}>
            항목을 클릭하면 상세 정보를 확인할 수 있습니다.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setTick((t) => t + 1)}
          disabled={loading}
          style={ghostBtn}
        >
          새로고침
        </button>
      </div>

      {error && (
        <div
          style={{
            padding: "12px 14px",
            borderRadius: 10,
            background: "#FEF2F2",
            border: "1px solid #FECACA",
            color: "#991B1B",
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {/* Stats grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12,
        }}
      >
        <StatCard
          label="전체 회원"
          value={loading ? "—" : stats.users}
          hint={
            loading
              ? ""
              : `농가 ${stats.farmers ?? 0} · 수리기사 ${stats.experts ?? 0}`
          }
        />
        <StatCard
          label="수리 요청 (전체)"
          value={loading ? "—" : stats.matchesTotal}
          hint={
            loading
              ? ""
              : `대기 ${stats.matchesPending ?? 0} · 매칭 ${
                  stats.matchesMatched ?? 0
                } · 완료 ${stats.matchesDone ?? 0}`
          }
        />
        <StatCard
          label="매칭 대기"
          value={loading ? "—" : stats.matchesPending}
          accent={C.amber800}
          hint="status = 0"
        />
        <StatCard
          label="1:1 문의"
          value={loading ? "—" : stats.servicesTotal}
          hint={
            loading
              ? ""
              : `대기 ${stats.servicesPending ?? 0} · 답변 ${
                  stats.servicesAnswered ?? 0
                }`
          }
        />
        <StatCard
          label="문의 답변 대기"
          value={loading ? "—" : stats.servicesPending}
          accent={C.amber800}
        />
        <StatCard
          label="커뮤니티 게시글"
          value={loading ? "—" : stats.communities}
        />
      </div>

      {/* Recent panels */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 14,
        }}
      >
        <Panel
          title="최근 가입 회원"
          count={recent.users?.length ?? 0}
          right={<MoreLink to="/miadmin/users" />}
        >
          {loading ? (
            <SkeletonRows />
          ) : (recent.users || []).length === 0 ? (
            <Empty />
          ) : (
            recent.users.map((u, i) => (
              <Row
                key={u.docId || i}
                onClick={() => navigate(`/miadmin/users/${u.docId}`)}
                left={
                  <span>
                    {u.name || u.id}
                    <span style={{ marginLeft: 8 }}>
                      <UserTypeChip userType={u.userType} />
                    </span>
                  </span>
                }
                mid={u.id}
                right={relativeTime(u.createDate)}
              />
            ))
          )}
        </Panel>

        <Panel
          title="최근 수리 요청"
          count={recent.matches?.length ?? 0}
          right={<MoreLink to="/miadmin/matches" />}
        >
          {loading ? (
            <SkeletonRows />
          ) : (recent.matches || []).length === 0 ? (
            <Empty />
          ) : (
            recent.matches.map((m, i) => (
              <Row
                key={m.docId || i}
                onClick={() => navigate(`/miadmin/matches/${m.docId}`)}
                left={
                  <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                    <span>{m.selectList || "—"}</span>
                    <MatchStatusChip status={m.status} />
                  </span>
                }
                mid={m.name || m.id}
                right={relativeTime(m.createDate)}
              />
            ))
          )}
        </Panel>

        <Panel
          title="최근 1:1 문의"
          count={recent.services?.length ?? 0}
          right={<MoreLink to="/miadmin/services" />}
        >
          {loading ? (
            <SkeletonRows />
          ) : (recent.services || []).length === 0 ? (
            <Empty />
          ) : (
            recent.services.map((s, i) => (
              <Row
                key={s.docId || i}
                onClick={() => navigate(`/miadmin/services/${s.docId}`)}
                left={
                  <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                      {s.title || "—"}
                    </span>
                    <ServiceStatusChip status={s.status} />
                  </span>
                }
                mid={s.type || s.name}
                right={relativeTime(s.createDate)}
              />
            ))
          )}
        </Panel>

        <Panel
          title="최근 커뮤니티 글"
          count={recent.communities?.length ?? 0}
          right={<MoreLink to="/miadmin/communities" />}
        >
          {loading ? (
            <SkeletonRows />
          ) : (recent.communities || []).length === 0 ? (
            <Empty />
          ) : (
            recent.communities.map((c, i) => (
              <Row
                key={c.docId || i}
                onClick={() => navigate(`/miadmin/communities/${c.docId}`)}
                left={c.title || "—"}
                mid={c.type || c.name}
                right={relativeTime(c.createDate)}
              />
            ))
          )}
        </Panel>
      </div>
    </>
  );
}

function MoreLink({ to }) {
  return (
    <Link
      to={to}
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: C.greenDark,
        textDecoration: "none",
      }}
    >
      전체 보기 →
    </Link>
  );
}
